const { ArgumentNullException } = require('exceptions');
const {
    isUfeEnvLocal,
    isUfeEnvQA
} = require('utils/Env');
const errorBoundary = require('utils/framework/hocs/withErrorBoundary');
const React = require('react');


const LocalOrQAEnvironment = (isUfeEnvQA || isUfeEnvLocal);
const HOCWrappingIsDisabled = Sephora.configurationSettings.core.disableHOCWrapping === true;

/**
 * Composes multi-argument HOCs/functions from right to left.
 * Same HOC/function arguments will be provided for every HOC/function while composing.
 * Implementation of `compose` function from `redux` library composes only single-argument HOCs/functions.
 * In case of exception in any HOC returns `React.Fragment`.
 *
 * @param {...Function} arguments The HOCs/functions to compose.
 *
 * @returns {Function} A HOC/function obtained by composing the argument HOCs/functions from right to left.
 */
function compose() {
    if (!arguments.length) {
        return arg => arg;
    }

    const hocs = [...arguments];
    const lastHOC = hocs.pop();

    function composeWithArgs() {
        try {
            const initialValue = lastHOC(...arguments);
            const [, ...hocArgs] = arguments;
            const result = hocs.reduceRight((component, hoc) => hoc(component, ...hocArgs), initialValue);

            return result;
        } catch (error) {
            // TODO: create a special error component to render different markup on prod and non prod environments.
            return React.Fragment;
        }
    }

    return composeWithArgs;
}

/**
 * Generic HOC's wrapper for any HOC in application.
 * Creates a new HOC by composing `withErrorBoundary`, `withUserHOC` (user provided HOC) and `withRenderTimeMeasurements` HOCs.
 * `withRenderTimeMeasurements` HOC will not be used during SSR and non local or QA environmets.
 *
 * Example of HOC's composition for `SSR`:
 * ```javascript
 * compose(withErrorBoundary, withUserHOC);
 * ```
 *
 * Example of HOC's composition for `local` environment:
 * ```javascript
 * compose(withErrorBoundary, withUserHOC, withRenderTimeMeasurements);
 * ```
 *
 * @param {Function} withUserHOC User provided HOC.
 *
 * @return {Function} A new HOC based on composition of `withErrorBoundary`, `withUserHOC` and `withRenderTimeMeasurements` (optionaly) HOCs.
 */
const wrapHOC = withUserHOC => {
    if (withUserHOC == null) {
        throw new ArgumentNullException('withUserHOC');
    }

    let hoc;

    if (HOCWrappingIsDisabled) {
        hoc = withUserHOC;
    } else {
        const withErrorBoundary = errorBoundary.createHOC(withUserHOC);

        if (!Sephora.isNodeRender && LocalOrQAEnvironment) {
            const { withRenderTimeMeasurements } = require('utils/framework/hocs/withRenderTimeMeasurements');
            hoc = compose(withErrorBoundary, withUserHOC, withRenderTimeMeasurements);
        } else {
            hoc = compose(withErrorBoundary, withUserHOC);
        }
    }

    return hoc;
};

module.exports = { wrapHOC };
